// ============================================================
// useLeavePageWarning.ts
// Shows the browser's native "Leave site? Changes you made may not be
// saved" prompt on reload / tab close / navigation-away while `active`.
//
// One listener is attached for the component's lifetime and reads the
// CURRENT `active` value from a ref at unload time — so it never races a
// re-render (no add/remove churn every timer tick) and it can't be left
// stale. Standard pattern: preventDefault() + returnValue = '' (both are
// needed across Chrome / Firefox / Safari; custom text is ignored).
//
// Note: browsers only honour the prompt after the user has interacted with
// the page (sticky activation) — starting a timer is such an interaction.
// ============================================================
import { useEffect, useRef } from 'react'

export const useLeavePageWarning = (active: boolean): void => {
  const activeRef = useRef(active)
  activeRef.current = active
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent): string | undefined => {
      if (!activeRef.current) return undefined
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])
}
